import Loading from "./components/Loading";

let gasState = createGASState();

/**
 * Cria um novo estado para a comunicação GAS.
 * @returns {Object} Estado inicial.
 */
function createGASState() {
    return {
        responses: {}
    };
}

/**
 * Registra o event listener para mensagens do iframe e retorna um novo estado.
 * @param {Object} state Estado atual.
 * @param {Function} callback Função de callback para tratar mensagens.
 * @returns {Object} Novo estado com listener registrado.
 */
function registerGASListener(state, callback) {
    const newState = {
        ...state,
        responses: { ...state.responses }
    };
    
    const handleMessage = (event) => callback(event);
    
    window.removeEventListener('message', handleMessage);
    window.addEventListener('message', handleMessage);
    console.debug('[GAS] Listener instalado no front-end');
    return newState;
}

/**
 * Envia uma requisição ao servidor GAS através do iframe.
 * @param {Object} state Estado atual.
 * @param {String} service Nome da função no servidor.
 * @param {Array} serviceParams Parâmetros da função.
 * @returns {Promise} Promessa que será resolvida quando a resposta for recebida.
 */
function requestGAS(state, service, ...serviceParams) {
    if (window.location !== window.parent.location) {
        const responseIndex = Object.entries(state.responses).length;
        console.debug(`[GAS] Inicializando GAS.request(${service}) no servidor com response nº{${responseIndex}}`);
        
        const message = {
            functionRunParams: {
                functionName: service,
                functionParams: serviceParams
            },
            functionCallbackName: responseIndex
        };
        
        Loading.loading(true);
        window.parent.postMessage(message, '*');

        return responseGAS(state, responseIndex);
    } else {
        const error = `A app não está carregada dentro de um iframe de projeto GAS, com URL /dev ou /exec`;
        console.error(error);
        return Promise.reject(new Error(error));
    }
}

/**
 * Aguarda a resposta do servidor GAS.
 * @param {Object} state Estado atual.
 * @param {Number} responseIndex Índice da resposta aguardada.
 * @returns {Promise} Promessa que será resolvida quando a resposta estiver pronta.
 */
function responseGAS(state, responseIndex) {
    state.responses[responseIndex] = {};
    return new Promise((resolve, reject) => {
        const responseIntervalId = setInterval(() => {
            if (state.responses[responseIndex].done) {
                clearInterval(state.responses[responseIndex].intervalId);
                const response = state.responses[responseIndex].response;
                delete state.responses[responseIndex];

                // Verifica se ainda há respostas em andamento e desativa o loading
                if (Object.keys(state.responses).length === 0) {
                    console.debug("[GAS] Finalizando loading");
                    Loading.loading(false); // Desativa o loading quando todas as requisições estiverem concluídas
                }

                if (hasCPAError(response)) {
                    reject(new Error(response.message));
                }
                resolve(response);

            } else {
                console.debug("[GAS] Loading ativo, aguardando resposta...");
                Loading.loading(Object.keys(state.responses).length !== 0);
            }
        }, 500);

        state.responses[responseIndex].done = false;
        state.responses[responseIndex].intervalId = responseIntervalId;
    });
}

/**
 * Callback para tratar a resposta recebida do iframe.
 * @param {Object} state Estado atual.
 * @param {Object} event Evento de mensagem.
 * @returns {Object} Novo estado atualizado.
 */
function callbackGAS(state, event) {
    const { functionCallbackName, functionCallbackParams } = event.data;

    console.debug(`[GAS] Inicializando GAS.callback() para response nº{${functionCallbackName}}`);

    if (state.responses[functionCallbackName] !== undefined) {
        // Atualiza a resposta e marca como concluída
        state.responses[functionCallbackName].response = functionCallbackParams;
        state.responses[functionCallbackName].done = true;
        console.debug(`[GAS] Resposta recebida e marcada como concluída para ${functionCallbackName}`);
    } else {
        console.error(`[GAS] Nenhuma resposta encontrada para o índice ${functionCallbackName}`);
    }

    return state;
}

/**
 * Verifica se a resposta contém um erro CPA.
 * @param {Object} responseObj Resposta a ser verificada.
 * @returns {Boolean} Retorna true se for um erro CPA.
 */
function hasCPAError(responseObj) {
    return responseObj instanceof Error && responseObj.message.includes('CPAError');
}

// Interface pública compatível com o uso original
export default class GAS {

    static _instance = null;

    constructor() {
        if (GAS._instance != null) {
            throw new Error('Esta classe não pode ser instanciada mais de uma vez');
        }

        // Registra o listener para mensagens
        gasState = registerGASListener(gasState, (messageEvent) => {
            gasState = callbackGAS(gasState, messageEvent);
        });

        console.debug('[GAS] Singleton GAS instanciado');
    }

    /**
     * Método para obter a instância única do GAS.
     * @returns {GAS} A instância única.
     */
    static getInstance() {
        if (GAS._instance === null) {
            GAS._instance = new GAS();
        }
        return GAS._instance;
    }

    /**
     * Método para realizar a requisição ao GAS.
     * @param {String} service Nome do serviço a ser chamado no GAS.
     * @param {...any} serviceParams Parâmetros do serviço.
     * @returns {Promise} Promessa que resolve com a resposta do GAS.
     */
    request(service, ...serviceParams) {
        return requestGAS(gasState, service, ...serviceParams);
    }
}
